import React from 'react';

// Dynamic import to handle Jest issues with react-markdown
let ReactMarkdown: any = null;
try {
  ReactMarkdown = require('react-markdown').default || require('react-markdown');
} catch (error) {
  console.warn('react-markdown not available:', error);
}

interface ContentRendererProps {
  content: React.ReactNode;
  isMarkdown?: boolean;
}

const ContentRenderer: React.FC<ContentRendererProps> = ({ content, isMarkdown = true }) => {
  // If content is not a string, render it as-is (for React nodes)
  if (typeof content !== 'string') {
    return <>{content}</>;
  }

  // If markdown is disabled or ReactMarkdown is not available, render with preserved newlines
  if (!isMarkdown || !ReactMarkdown) {
    return (
      <div style={{ whiteSpace: 'pre-wrap' }}>
        {content}
      </div>
    );
  }

  // Render with react-markdown
  return (
    <ReactMarkdown
      components={{
        // Custom paragraph component to handle spacing
        p: ({ children }: any) => <p style={{ margin: '0 0 8px 0' }}>{children}</p>,
        
        // Custom list components for better spacing
        ul: ({ children }: any) => <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>{children}</ul>,
        ol: ({ children }: any) => <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>{children}</ol>,
        li: ({ children }: any) => <li style={{ margin: '4px 0' }}>{children}</li>,
        
        // Custom code block styling
        pre: ({ children }: any) => (
          <pre style={{
            backgroundColor: '#f6f8fa',
            border: '1px solid #e1e4e8',
            borderRadius: '6px',
            fontSize: '85%',
            lineHeight: '1.45',
            margin: '8px 0',
            overflow: 'auto',
            padding: '16px',
            color: '#24292e'
          }}>
            {children}
          </pre>
        ),
        
        // Inline code styling
        code: ({ children, className }: any) => {
          const isCodeBlock = className?.includes('language-');
          
          if (isCodeBlock) {
            return <code className={className}>{children}</code>;
          }
          
          return (
            <code style={{
              backgroundColor: 'rgba(175, 184, 193, 0.2)',
              borderRadius: '3px',
              fontSize: '85%',
              margin: 0,
              padding: '0.2em 0.4em',
              color: '#24292e'
            }}>
              {children}
            </code>
          );
        },
        
        // Header styling
        h1: ({ children }: any) => <h1 style={{ margin: '16px 0 8px 0', fontSize: '1.5em' }}>{children}</h1>,
        h2: ({ children }: any) => <h2 style={{ margin: '16px 0 8px 0', fontSize: '1.3em' }}>{children}</h2>,
        h3: ({ children }: any) => <h3 style={{ margin: '16px 0 8px 0', fontSize: '1.1em' }}>{children}</h3>,
        h4: ({ children }: any) => <h4 style={{ margin: '12px 0 6px 0', fontSize: '1em' }}>{children}</h4>,
        h5: ({ children }: any) => <h5 style={{ margin: '12px 0 6px 0', fontSize: '0.9em' }}>{children}</h5>,
        h6: ({ children }: any) => <h6 style={{ margin: '12px 0 6px 0', fontSize: '0.8em' }}>{children}</h6>,
        
        // Blockquote styling
        blockquote: ({ children }: any) => (
          <blockquote style={{
            borderLeft: '4px solid #dfe2e5',
            color: '#6a737d',
            margin: '8px 0',
            paddingLeft: '16px'
          }}>
            {children}
          </blockquote>
        ),
        
        // Table styling
        table: ({ children }: any) => (
          <table style={{
            borderCollapse: 'collapse',
            margin: '8px 0',
            width: '100%'
          }}>
            {children}
          </table>
        ),
        th: ({ children }: any) => (
          <th style={{
            border: '1px solid #dfe2e5',
            padding: '6px 13px',
            backgroundColor: '#f6f8fa'
          }}>
            {children}
          </th>
        ),
        td: ({ children }: any) => (
          <td style={{
            border: '1px solid #dfe2e5',
            padding: '6px 13px'
          }}>
            {children}
          </td>
        )
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

export default ContentRenderer;
